<template>
  <Card padding="none">
    <div class="px-6 py-5">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Neural Credits
        </h3>
        <div
          v-if="isLowBalance"
          class="flex items-center text-amber-600 dark:text-amber-400 animate-pulse"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <span class="text-xs font-medium">Low Balance</span>
        </div>
      </div>

      <div class="flex items-baseline">
        <span class="text-3xl font-bold text-gray-900 dark:text-white">
          {{ formattedBalance }}
        </span>
        <span class="ml-2 text-sm text-gray-500 dark:text-gray-400">credits</span>
      </div>

      <div class="mt-4 flex items-center text-sm text-gray-500 dark:text-gray-400">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4 mr-1.5 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>Expires: {{ formattedExpiry }}</span>
      </div>
    </div>

    <template #footer>
      <Button full-width @click="$emit('top-up')">Buy Credits</Button>
    </template>
  </Card>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import type { OrganizationCredits } from '../types';
  import { Card, Button } from '@asabdev/asab-ui';

  interface Props {
    credits: OrganizationCredits | null;
    lowBalanceThreshold?: number;
  }

  const props = withDefaults(defineProps<Props>(), {
    lowBalanceThreshold: 100,
  });

  defineEmits<{
    (e: 'top-up'): void;
  }>();

  const formattedBalance = computed(() => {
    if (!props.credits) return '0';
    return new Intl.NumberFormat().format(props.credits.balance);
  });

  const formattedExpiry = computed(() => {
    if (!props.credits?.expiry) return 'N/A';
    try {
      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(new Date(props.credits.expiry));
    } catch {
      return props.credits.expiry;
    }
  });

  const isLowBalance = computed(() => {
    if (!props.credits) return true;
    return props.credits.balance <= props.lowBalanceThreshold;
  });
</script>
